/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Ember Imports
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty, typeOf } from '@ember/utils';
// Internal Imports
import PageAccessRoles from '@linc-technologies/linced-common/enums/page-access-level';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import ResponseActivityGroup from '@linc-technologies/linced-common/enums/response-activity-group';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { snapshotFields } from '@linc-technologies/linced-common/utils/record-rollback';
import { onAttachmentUpload } from '@linc-technologies/linced-common/utils/uppy';
import { getResponseActivityDetailErrors } from '../utils/response-activity-validation';
// External Imports
import moment from 'moment/moment';

export default class AddEditResponseActivity extends Component {
  // Services
  @service
  googleDrive;

  @service
  roles;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @service
  uppy;

  ResponseActivityGroup = ResponseActivityGroup;
  @tracked
  attachments = [];
  @tracked
  responseActivityDialogOpen = false;
  @tracked
  responseActivity = null;
  todayString = moment().format('DD/MM/YYYY');
  updateResponseActivities() {}

  detailOptions = Object.freeze({
    1: [
      'Sent notification to whānau',
      'Sent escalated notification to whānau',
      'Sent formal warning notice to whānau',
      'Sent letter acknowledging upcoming absence',
    ],
    2: [
      'Initial conversation with whānau',
      'Escalated conversation with whānau',
      'Held whānau hui',
    ],
    3: [], // Developed individual support plan – free text only
    4: [
      'Reduced transport barriers',
      'Reduced food barriers',
      'Reduced clothing barriers',
      'Reduced equipment barriers',
      'Referred to in-school social worker',
      'Referred to in-school counsellor',
      'Referred to in-school mentor',
      'Reduced other barriers',
    ],
    5: [
      'Reinforcement conversation',
      'Reinforcement letter/certificate',
      'Other positive action',
    ],
    6: [
      'Referred to attendance services',
      'Referred to local provider',
      'Referred to iwi provider',
      'Referred to alternative education/activity centre',
      'Referred to Ministry regional office',
      'Referred to external mental health support',
      'Referred to other education agency',
      'Referred to other non-education agency',
    ],
    7: [],
    8: [],
    9: [],
    99: [],
  });

  @computed('session.person', 'responseActivity.recordedBy')
  get ActivityRecordedBy() {
    if (!isEmpty(this.responseActivity.recordedBy)) {
      return this.responseActivity.recordedBy;
    }
    return this.get('session.person');
  }

  @computed('responseActivityConfig')
  get configuredDetailOptions() {
    // If there are no response activity configurations, return null so we use the default detail options.
    if (isEmpty(this.responseActivityConfig)) {
      return null;
    }

    let configuration = this.responseActivityConfig[0].configuration;
    return configuration.reduce((options, option = {}) => {
      let key = option.responseActivityGroup;
      let details = option.details || [];
      options[key] = details;
      return options;
    }, {});
  }

  @computed('configuredDetailOptions')
  get availableDetailOptions() {
    return this.configuredDetailOptions || this.detailOptions;
  }

  completeDateValidations = Object.freeze([
    {
      message: 'Completed Date must be in the past',
      validate: (val) => {
        let parsed = moment(val);
        let now = moment();
        return parsed.isValid() && parsed.isSameOrBefore(now, 'd');
      },
    },
  ]);

  @computed('responseActivity.detail')
  get detailErrors() {
    return getResponseActivityDetailErrors(this.get('responseActivity.detail'));
  }

  @action
  handleResponseActivityDialogOpen(responseActivity) {
    if (isEmpty(responseActivity)) {
      responseActivity = this.store.createRecord(
        'attendance/response-activity',
        {
          schoolId: this.get('session.school'),
          personId: this.learner,
          recordedById: this.get('session.person'),
        },
      );
    }
    this.responseActivity = responseActivity;
    this.responseActivityDialogOpen = true;

    // Initialise attachments array
    this.attachments = [];
    this.loadAttachments(responseActivity);
    this.initUppy();
  }

  @action
  async saveResponseActivity() {
    this.saveInProgress = true;
    // Media is linked before the save; if it fails, unlink it again, since
    // the refetch in finally no longer drops unsaved hasMany edits. The
    // dialog keeps the attachments for a retry.
    const restoreMedia = snapshotFields(this.responseActivity, ['mediaIds']);

    try {
      if (this.responseActivity.id) {
        await this.saveExistingResponseActivity();
      } else {
        await this.saveNewResponseActivity();
      }

      this.responseActivityDialogOpen = false;
      this.responseActivity = null;
      this.toaster.success(`Response activity saved successfully.`);
    } catch {
      restoreMedia();
      this.toaster.error(`Error saving response activity.`);
    } finally {
      this.updateResponseActivities();
      this.saveInProgress = false;
    }
  }

  @action
  clearActivityCompletedBy() {
    this.responseActivity.set('completedBy', null);
  }

  @action
  setResponseActivity(option) {
    // reset the detail to null if the response activity changes
    this.responseActivity.set('detail', null);

    this.responseActivity.set('responseActivity', option.value);
  }

  @action
  setResponseActivityDetail(option) {
    this.responseActivity.set('detail', option);
  }

  @action
  getLastModifiedDate(responseActivity) {
    let timestamp = responseActivity.updateTime || responseActivity.createTime;

    if (!timestamp) {
      return this.todayString;
    }

    return moment.unix(timestamp).format('DD/MM/YYYY');
  }

  @action
  GetNotesAccess(responseActivity) {
    if (responseActivity.hideNotes) {
      return (
        this.roles.has(PageAccessRoles.SENSITIVE) ||
        this.roles.has(PageAccessRoles.CONFIDENTIAL)
      );
    }
    return true;
  }

  @action
  addImages() {
    this.uppy.addImages();
  }

  @action
  addVideos() {
    this.uppy.addVideos();
  }

  @action
  addFiles() {
    this.uppy.addFiles();
  }

  @action
  removeAttachment(attachment) {
    this.attachments = this.attachments.filter((a) => a !== attachment);
  }

  /**
   * Launch's the Google Drive picker...
   * @param {*} postImmediately - if set to true, then automatically post to the feed once complete.
   */
  @action
  launchGooglePicker(postImmediately = false) {
    window.launchPickerCallback = this;

    this.postImmediately = postImmediately;
    this.googleDrive
      .pickFiles()
      .then((files) => this.embedDriveFiles(files))
      .catch((err) => this.googleDrive.showEmbedError(err));
  }

  @action
  closeGoogleDrivePermissionDialog(action) {
    if (typeOf(action) === 'function') {
      action();
    }
    this.googleDrivePermissionDialogOpen = false;
  }

  @action
  noop() {}

  @tracked
  googleDrivePermissionDialogOpen = false;
  @tracked
  googleDrivePermissionStatus = null;
  @tracked
  googleDrivePermissionResolve = null;
  @tracked
  googleDrivePermissionReject = null;

  @tracked
  postImmediately;

  @tracked
  saveInProgress;

  // embedDriveFiles adds the picked Google Drive files as attachments.
  embedDriveFiles(files) {
    for (const file of files) {
      this.googleDrive
        .embedFile(file, (status) => {
          // The user can still embed the file in the permission dialog.
          return new Promise((resolve, reject) => {
            this.googleDrivePermissionDialogOpen = true;
            this.googleDrivePermissionStatus = status;
            this.googleDrivePermissionResolve = resolve;
            // Closing the dialog is a cancel, which needs no message.
            this.googleDrivePermissionReject = () =>
              reject({ userCancelled: true });
          });
        })
        .then((media) => {
          if (!media) {
            return;
          }

          let record = this.store.createRecord('media', {
            ...media,
            schoolId: this.get('session.school'),
            personId: this.get('session.person'),
            sharedWith: SharingLevel.SCHOOL,
          });

          this.attachments = [...this.attachments, { record }];
        })
        .catch((err) => this.googleDrive.showEmbedError(err, file));
    }
  }

  /**
   * loadAttachments populates the attachments array from the response
   * activity's saved media.
   *
   * `media` is an async `hasMany`, so the first time a response activity is
   * opened its media records have not been fetched yet and the relationship
   * iterates as empty. Wait for it to resolve before populating, otherwise
   * nothing shows until a later open happens to find the records cached.
   * An upload can land in this.attachments while the media is loading, so
   * merge the loaded records in rather than replacing the array.
   * @param {*} responseActivity - the response activity being edited.
   */
  async loadAttachments(responseActivity) {
    if (isEmpty(responseActivity)) {
      return;
    }

    try {
      let media = await responseActivity.media;

      // The dialog may have been closed, or reopened against another response
      // activity, while the media was loading. Uploads replace
      // this.attachments, so compare the response activity rather than the
      // array.
      if (this.isDestroyed || this.isDestroying) {
        return;
      }
      if (this.responseActivity !== responseActivity) {
        return;
      }

      let existing = this.attachments;
      let loaded = media
        .filter((record) => !findBy(existing, 'record.id', record.id))
        .map((record) => ({ record }));
      this.attachments = [...loaded, ...existing];
    } catch {
      if (this.isDestroyed || this.isDestroying) {
        return;
      }
      this.toaster.error(`Error loading response activity media.`);
    }
  }

  /**
   * saveExistingResponseActivity saves a response activity that has already
   * been persisted, reconciling its media with the attachments the user has
   * added or removed in the dialog.
   */
  async saveExistingResponseActivity() {
    let attachments = this.attachments;

    // Wait for the media relationship before diffing it against the
    // attachments - an unresolved relationship iterates as empty, which would
    // look like every existing attachment had been removed.
    let media = await this.responseActivity.media;

    let mediaToRemove = media.filter(
      (record) => !findBy(attachments, 'record.id', record.id),
    );
    let newAttachments = attachments.filter(
      (attachment) => attachment.record.isNew,
    );

    await Promise.all([
      ...mediaToRemove.map((record) => record.destroyRecord()),
      ...newAttachments.map((attachment) => attachment.record.save()),
    ]);

    attachments.forEach((attachment) => {
      let record = attachment.record;
      if (!media.includes(record)) {
        media.push(record);
      }
    });

    return this.responseActivity.save();
  }

  /**
   * saveNewResponseActivity saves a response activity for the first time, then
   * associates any attachments with it and re-saves, as the attachments need
   * the response activity's ID.
   */
  async saveNewResponseActivity() {
    await this.responseActivity.save();

    let attachments = this.attachments;
    if (!attachments.length) {
      return;
    }

    // Save the attachments first so that we have their IDs.
    await Promise.all(
      attachments.map((attachment) => attachment.record.save()),
    );

    // media is an async hasMany: push onto the loaded ManyArray, not the
    // PromiseManyArray.
    let media = await this.responseActivity.media;
    attachments.forEach((attachment) => {
      media.push(attachment.record);
    });

    return this.responseActivity.save();
  }

  initUppy() {
    this.uppy.setupAttachments((file, upload, mediaType) => {
      let attachment = onAttachmentUpload(
        this.store,
        SharingLevel.SCHOOL,
        this.get('session.school'),
        this.get('session.person'),
        file,
        upload,
        mediaType,
      );
      this.attachments = [...this.attachments, attachment];
    });
  }
}
